import React from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { api } from '../api';
import { Purchase, Item } from '../types';

interface StatisticsPageProps {
  selectedDate?: string;
}

interface StatItem {
  fruitTypeName: string;
  price: number;
  quantity: number;
  totalAmount: number;
  fruitTypeId: string;
}

interface GroupedStatistics {
  wholesale: StatItem[];
  retail: StatItem[];
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
  }).format(value);
};

const formatQuantity = (value: number): string => {
  return value.toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

export const StatisticsPage: React.FC<StatisticsPageProps> = ({ selectedDate }) => {
  const [statistics, setStatistics] = React.useState<GroupedStatistics>({
    wholesale: [],
    retail: [],
  });
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    loadStatistics();
  }, [selectedDate]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const purchasesData = await api.getPurchases(selectedDate);
      const grouped = groupByCustomerTypeAndFruit(purchasesData);
      setStatistics(grouped);
    } catch (err) {
      console.error('Error loading statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const groupByCustomerTypeAndFruit = (purchases: Purchase[]): GroupedStatistics => {
    const wholesaleMap = new Map<string, StatItem>();
    const retailMap = new Map<string, StatItem>();

    purchases.forEach((purchase) => {
      const customerType = purchase.customer?.customerType || 'Unknown';
      const isWholesale = customerType.toLowerCase() === 'wholesale';
      const targetMap = isWholesale ? wholesaleMap : retailMap;

      (purchase.items || []).forEach((item: Item) => {
        const key = item.fruitTypeId;
        const existing = targetMap.get(key);

        if (existing) {
          existing.quantity += item.weight;
          existing.totalAmount += item.total;
        } else {
          targetMap.set(key, {
            fruitTypeName: item.fruitTypeName,
            fruitTypeId: item.fruitTypeId,
            price: item.price,
            quantity: item.weight,
            totalAmount: item.total,
          });
        }
      });
    });

    const sortByName = (items: StatItem[]) =>
      items.sort((a, b) => a.fruitTypeName.localeCompare(b.fruitTypeName));

    return {
      wholesale: sortByName(Array.from(wholesaleMap.values())),
      retail: sortByName(Array.from(retailMap.values())),
    };
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Loại trái cây</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Giá (VNĐ)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Số lượng (kg)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Thành tiền (VNĐ)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Thương lái */}
              {statistics.wholesale.length > 0 && (
                <>
                  <TableRow sx={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1.1em', color: '#e65100' }}>
                      👨🏻‍💼 Thương lái
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                      {formatQuantity(statistics.wholesale.reduce((sum, item) => sum + item.quantity, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                      {formatCurrency(statistics.wholesale.reduce((sum, item) => sum + item.totalAmount, 0))}
                    </TableCell>
                  </TableRow>
                  {statistics.wholesale.sort((a, b) => a.fruitTypeId > b.fruitTypeId ? 1 : -1).map((item, index) => (
                    <TableRow key={`wholesale-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                      <TableCell sx={{ pl: 4, py: 1 }}>{item.fruitTypeName}</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>{formatCurrency(item.price)}</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>{formatQuantity(item.quantity)}</TableCell>
                      <TableCell align="right" sx={{ py: 1, fontWeight: 600, color: 'success.main' }}>{formatCurrency(item.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </>
              )}

              {/* Khách lẻ */}
              {statistics.retail.length > 0 && (
                <>
                  <TableRow sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1.1em', color: '#1565c0' }}>
                      🙍‍♂️ Khách lẻ
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                      {formatQuantity(statistics.retail.reduce((sum, item) => sum + item.quantity, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                      {formatCurrency(statistics.retail.reduce((sum, item) => sum + item.totalAmount, 0))}
                    </TableCell>                  </TableRow>
                  {statistics.retail.sort((a, b) => a.fruitTypeId > b.fruitTypeId ? 1 : -1).map((item, index) => (
                    <TableRow key={`retail-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                      <TableCell sx={{ pl: 4, py: 1 }}>{item.fruitTypeName}</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>{formatCurrency(item.price)}</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>{formatQuantity(item.quantity)}</TableCell>
                      <TableCell align="right" sx={{ py: 1, fontWeight: 600, color: 'success.main' }}>{formatCurrency(item.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </>
              )}

              {/* Grand Total */}
              {(statistics.wholesale.length > 0 || statistics.retail.length > 0) && (
                <TableRow sx={{ backgroundColor: '#c8e6c9', fontWeight: 'bold', fontSize: '1.1em' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TỔNG CỘNG</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatQuantity(
                      statistics.wholesale.reduce((sum, item) => sum + item.quantity, 0) +
                      statistics.retail.reduce((sum, item) => sum + item.quantity, 0)
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(
                      statistics.wholesale.reduce((sum, item) => sum + item.totalAmount, 0) +
                      statistics.retail.reduce((sum, item) => sum + item.totalAmount, 0)
                    )}
                  </TableCell>
                </TableRow>
              )}

              {statistics.wholesale.length === 0 && statistics.retail.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#999' }}>
                    Không có dữ liệu
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
